import { voiceReplyParts } from '@common/helpers/arcVoice'
import {
  ARC_VOICE_AGENDA_MAX_ITEMS,
  ARC_VOICE_AGENDA_TEXT_MAX_CHARS,
  ArcVoiceAnswerStatus,
  ArcVoiceCallItemState,
  IArcVoiceAnswer,
  IArcVoiceCallAgendaEntry,
} from '@common/interfaces/arcVoice'

export enum ArcVoiceAgendaStatus {
  Working = 'working',
  /** Settled, and waiting for a pause in the conversation to be told. */
  Ready = 'ready',
  /** Mentioned at a pause ("the Ben invite is back — want it now?"); details not given yet. */
  Offered = 'offered',
  /** Set aside to come back to later in the call, when they said so. */
  Parked = 'parked',
  Told = 'told',
  /** Cancelled at their word before it finished. */
  Stopped = 'stopped',
}

/** How a settled result is brought up at the next pause. */
export enum ArcVoiceDelivery {
  /** What they're waiting on, or something that needs them: say it. */
  Tell = 'tell',
  /** Something from earlier while they're on another thing: mention it and ask. */
  Offer = 'offer',
  /** Already on their screen, or overtaken by a correction: kept in mind, not said. */
  Silent = 'silent',
}

/** What the turn behind an item did that the person could see. */
export interface IAgendaItemEffects {
  wrote: boolean
  navigated: boolean
}

/** One thing asked on the call: what the voice, the caller and the screen all call it by. */
export interface IArcVoiceAgendaItem {
  /** The Arc turn doing the work. */
  id: string
  /** Its place on the call's agenda, from 1. */
  number: number
  title: string
  request: string
  status: ArcVoiceAgendaStatus
  /** How it went, once settled. */
  outcome: ArcVoiceAnswerStatus | null
  progress: string | null
  /** What Arc found, in the words it would say. */
  result: string | null
  /** A draft Arc wrote for them, read out word for word when the result is told. */
  draft?: string | null
  /** The draft was too long to read whole, so `draft` is only its opening. */
  isDraftCut?: boolean
  /** The earlier item this one changes or adds to. */
  followsUp: number | null
  /** The Arc turn now doing its work, when that isn't the one that started it (after an approval). */
  turnId?: string | null
  /** Turns it ran on before `turnId`, besides the one that started it: each owns plan steps and subagents a stop must reach. */
  earlierTurnIds?: string[]
  /** The Arc session its turns run in, which stopping it is keyed on. */
  sessionId?: string | null
  /** Came in on its own, from work Arc ran in the background, rather than being asked for. */
  isBackground?: boolean
  askedAt: number
  updatedAt: number
}

const TITLE_MAX_CHARS = 60
const TITLE_FALLBACK_WORDS = 6
const BRIEFING_MAX_ITEMS = 20
/** Long enough for a follow-up ("which customer was second?") to be answered without a lookup. */
const BRIEFING_RESULT_MAX_CHARS = 320

export const NO_TOOLS = 'Speak as yourself, in the first person, and do not call any tool.'

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text

/** The voice's title for a request, or the start of the request when it gave none. */
export const agendaTitle = (title: unknown, request: string): string => {
  const given = typeof title === 'string' ? title.replace(/[[\]"]/g, '').trim() : ''
  if (given) return truncate(given, TITLE_MAX_CHARS)
  const words = request.trim().split(/\s+/)
  const start = words.slice(0, TITLE_FALLBACK_WORDS).join(' ')
  return truncate(words.length > TITLE_FALLBACK_WORDS ? `${start}…` : start, TITLE_MAX_CHARS)
}

export const addAgendaItem = (
  agenda: IArcVoiceAgendaItem[],
  item: Pick<IArcVoiceAgendaItem, 'id' | 'title' | 'request' | 'sessionId'> & {
    followsUp: unknown
    at: number
  },
): IArcVoiceAgendaItem[] => {
  const followsUp = agenda.find(({ number }) => number === Number(item.followsUp))?.number ?? null
  return [
    ...agenda,
    {
      id: item.id,
      number: (agenda.at(-1)?.number ?? 0) + 1,
      title: item.title,
      request: item.request,
      status: ArcVoiceAgendaStatus.Working,
      outcome: null,
      progress: null,
      result: null,
      followsUp,
      sessionId: item.sessionId,
      askedAt: item.at,
      updatedAt: item.at,
    },
  ]
}

export const updateAgendaItem = (
  agenda: IArcVoiceAgendaItem[],
  id: string,
  patch: Partial<IArcVoiceAgendaItem>,
): IArcVoiceAgendaItem[] => agenda.map((item) => (item.id === id ? { ...item, ...patch } : item))

/** Set aside before its result came back: it keeps its place when the result lands. */
const SET_ASIDE_STATUSES = new Set([ArcVoiceAgendaStatus.Parked, ArcVoiceAgendaStatus.Offered])

export const settleAgendaItem = (
  agenda: IArcVoiceAgendaItem[],
  { chatHistoryId, status, spokenAnswer, draft, isDraftCut }: IArcVoiceAnswer,
  at: number,
): IArcVoiceAgendaItem[] => {
  const current = agenda.find(({ id }) => id === chatHistoryId)?.status
  return chatHistoryId
    ? updateAgendaItem(agenda, chatHistoryId, {
        status: current && SET_ASIDE_STATUSES.has(current) ? current : ArcVoiceAgendaStatus.Ready,
        outcome: status,
        result: spokenAnswer,
        draft,
        isDraftCut,
        progress: null,
        updatedAt: at,
      })
    : agenda
}

/** Arc may still be on it: working, or set aside before its result came back. Each is followed to its end. */
export const isAwaitingResult = ({ status, outcome }: IArcVoiceAgendaItem): boolean =>
  status === ArcVoiceAgendaStatus.Working || (SET_ASIDE_STATUSES.has(status) && outcome == null)

export const workingItems = (agenda: IArcVoiceAgendaItem[]) =>
  agenda.filter(({ status }) => status === ArcVoiceAgendaStatus.Working)

/** Settled items not yet told: approvals first, since they hold Arc up, then oldest first. */
export const readyToTell = (agenda: IArcVoiceAgendaItem[]): IArcVoiceAgendaItem[] =>
  agenda
    .filter(({ status }) => status === ArcVoiceAgendaStatus.Ready)
    .sort(
      (a, b) =>
        Number(b.outcome === ArcVoiceAnswerStatus.AwaitingApproval) -
          Number(a.outcome === ArcVoiceAnswerStatus.AwaitingApproval) || a.number - b.number,
    )

/** The step to show while Arc works: the latest one, named when more than one thing is running. */
export const agendaProgress = (agenda: IArcVoiceAgendaItem[]): string | null => {
  const working = workingItems(agenda)
  const latest = [...working]
    .filter(({ progress }) => progress)
    .sort((a, b) => a.updatedAt - b.updatedAt)
    .at(-1)
  if (!latest?.progress) return null
  return working.length > 1 ? `${latest.title} · ${latest.progress}` : latest.progress
}

/**
 * How a settled result is brought up. It is told when it is what they last asked
 * for or when it needs them (an approval, a failure). One from earlier is
 * mentioned with a question only when it is an answer they haven't seen: a
 * change or a page it opened is already on their screen, and one they corrected
 * since is overtaken, so those are kept in mind and not said.
 */
export const deliveryFor = (
  item: IArcVoiceAgendaItem,
  agenda: IArcVoiceAgendaItem[],
  effects: IAgendaItemEffects = { wrote: false, navigated: false },
): ArcVoiceDelivery => {
  const needsThem =
    item.outcome === ArcVoiceAnswerStatus.AwaitingApproval ||
    item.outcome === ArcVoiceAnswerStatus.Failed
  if (needsThem) return ArcVoiceDelivery.Tell
  // Nobody is waiting on it in this moment, so it is mentioned, never cut in with.
  if (item.isBackground) return ArcVoiceDelivery.Offer
  if (agenda.some(({ followsUp }) => followsUp === item.number)) return ArcVoiceDelivery.Silent
  const latest = Math.max(...agenda.map(({ number }) => number))
  if (item.number === latest) return ArcVoiceDelivery.Tell
  if (effects.wrote || effects.navigated) return ArcVoiceDelivery.Silent
  return ArcVoiceDelivery.Offer
}

const briefingState = ({ status, outcome, progress, result }: IArcVoiceAgendaItem): string => {
  if (status === ArcVoiceAgendaStatus.Stopped)
    return 'stopped at their word; nothing more will happen for it'
  if (status === ArcVoiceAgendaStatus.Parked) {
    const back = outcome
      ? `; it is back${result ? `: ${truncate(result, BRIEFING_RESULT_MAX_CHARS)}` : ''}`
      : ''
    return `parked: they want to come back to it later in this call; you will be prompted when${back}`
  }
  if (status === ArcVoiceAgendaStatus.Working) {
    return progress ? `still working (${progress})` : 'still working'
  }
  if (outcome === ArcVoiceAnswerStatus.Failed) return "didn't go through"
  if (outcome === ArcVoiceAnswerStatus.AwaitingApproval) {
    return 'prepared, waiting on their approval on screen'
  }
  const found = result ? `: ${truncate(result, BRIEFING_RESULT_MAX_CHARS)}` : ''
  if (status === ArcVoiceAgendaStatus.Ready) {
    return `back, not brought up yet. If they ask about it, it is done: tell them now; otherwise wait to be told when${found}`
  }
  if (status === ArcVoiceAgendaStatus.Offered) {
    return `back, and you mentioned it; they haven't heard the details yet${found}`
  }
  return `done and told${found}`
}

/** The agenda as the voice keeps it in mind, so "what's left?" gets a true answer. */
export const agendaBriefing = (agenda: IArcVoiceAgendaItem[]): string => {
  if (agenda.length === 0) {
    return 'Your agenda on this call is empty: nothing is running. Anything they ask for only starts when you call the tool.'
  }
  const lines = agenda.slice(-BRIEFING_MAX_ITEMS).map((item) => {
    const followsUp = item.followsUp ? ` (follows up #${item.followsUp})` : ''
    return `#${item.number} ${item.title}${followsUp}: ${briefingState(item)}`
  })
  return [
    "Your agenda on this call, oldest first. It is how you keep track of what's done and what's still running; never read it out as a list:",
    ...lines,
  ].join('\n')
}

/**
 * A settled result as a note in the voice's own conversation. It has to be an
 * item, not instructions on one reply: per-reply instructions aren't kept, so the
 * voice forgot results it had been handed and went on saying it was still waiting.
 */
export const resultNote = ({
  number,
  title,
  request,
  outcome,
  result,
  draft,
  isDraftCut,
}: IArcVoiceAgendaItem): string => {
  const about = `[Not said by the user — the result of #${number} "${title}", asked as "${request}"]`
  if (outcome === ArcVoiceAnswerStatus.Failed)
    return `${about} ${result ?? "It didn't go through."}`
  const found = result ?? 'It finished; the full write-up is on screen in your chat with them.'
  const approval =
    outcome === ArcVoiceAnswerStatus.AwaitingApproval
      ? ' Something you prepared is waiting on their approval on screen in the chat.'
      : ''
  const draftLabel = isDraftCut
    ? 'The start of the draft, word for word (the rest is on screen in the chat)'
    : 'The draft, word for word'
  const draftText = draft ? `\n\n${draftLabel}:\n${draft}` : ''
  return `${about} ${found}${approval}${draftText}`
}

/** The steer for reading a draft aloud: in full, or as far as it was given when it was cut. */
const draftPrompt = ({ isDraftCut, outcome }: IArcVoiceAgendaItem, naming: string): string => {
  const reading = isDraftCut
    ? "then read the part of the draft you were given word for word, exactly as written: don't summarise or shorten it. It is only the start of a long draft, so tell them it goes on and the full draft is on screen in the chat."
    : "then read the draft out in full, word for word, exactly as written: don't summarise or shorten it."
  const approval =
    outcome === ArcVoiceAnswerStatus.AwaitingApproval
      ? ' Then tell them in one sentence that it is waiting on their approval, which they can give on screen, and ask whether they want any changes first.'
      : ' Then ask whether they want any changes.'
  return `You just got the result above, with a draft they asked for. They may not be able to look at the screen, so say in a few words what it is, ${reading}${approval}${naming} ${NO_TOOLS}`
}

/**
 * The steer for telling a result. Talked through, never read out. It is named
 * only when something else is still running, so the caller can tell which it is;
 * otherwise naming it is just noise.
 */
export const answerPrompt = (item: IArcVoiceAgendaItem, isOtherWorkOpen: boolean): string => {
  if (item.outcome === ArcVoiceAnswerStatus.Failed) {
    const what = item.result
      ? 'you lost track of the lookup you were running'
      : "the lookup you just got back didn't go through"
    return `Tell them ${what}, in one short sentence${isOtherWorkOpen ? `, saying which one in plain words ("${item.title}")` : ''}, and offer to try again. ${NO_TOOLS}`
  }
  const naming = isOtherWorkOpen
    ? ` Other things are still running, so make clear in passing which one this is (about "${item.title}"), the way a person would ("The BBC summary's back —"), not as a label.`
    : ''
  if (item.draft) return draftPrompt(item, naming)
  return `You just got the result above. Tell them the gist conversationally, in your own words, in one to three short sentences: lead with the answer, no lists or exact figures beyond the one or two that matter, and mention the detail is on screen if there is more. Do not read it out word for word.${naming} ${NO_TOOLS}`
}

/** The steer for mentioning a result from earlier, without dropping the thread they're on. */
export const offerPrompt = (item: IArcVoiceAgendaItem): string =>
  `Something from earlier is back: "${item.title}". At this pause, mention it in one short sentence, the way a colleague would ("By the way, the Ben invite is done"), then ask whether they want to go into it now or keep going with what you're on. Don't give the result yet; it's above for when they say yes. ${NO_TOOLS}`

/** The steer for bringing a parked item back at a lull. */
export const parkedNudgePrompt = (item: IArcVoiceAgendaItem): string =>
  `They asked to come back to "${item.title}" later in this call, and things have gone quiet. In one short sentence, remind them and ask whether to pick it up now ("You wanted to come back to the February note — now a good time?"). ${NO_TOOLS}`

/** One short spoken update on a request still running. */
export const narrationPrompt = (item: IArcVoiceAgendaItem, isOnlyOpen: boolean): string => {
  const naming = isOnlyOpen ? '' : `, saying in passing which one ("${item.title}")`
  return `You're still working on "${item.title}"; the current step is "${item.progress}". Give one short update in plain words, under twelve words${naming} (for example "Still pulling the numbers."). ${NO_TOOLS}`
}

/** An Arc turn the agenda already follows: one an item started, or one carrying an item on. */
export const isOnAgenda = (agenda: IArcVoiceAgendaItem[], arcTurnId: string): boolean =>
  agenda.some(({ id, turnId }) => id === arcTurnId || turnId === arcTurnId)

/** A result that arrived from background work, as an agenda item ready to be mentioned. */
export const backgroundItem = (
  agenda: IArcVoiceAgendaItem[],
  { turnId, reply, at }: { turnId: string; reply: string; at: number },
): IArcVoiceAgendaItem[] => {
  const { spoken: result } = voiceReplyParts(reply)
  const added = addAgendaItem(agenda, {
    id: turnId,
    title: agendaTitle(undefined, result || 'Background work'),
    request: 'Work running in the background',
    followsUp: null,
    at,
  })
  return updateAgendaItem(added, turnId, {
    status: ArcVoiceAgendaStatus.Ready,
    outcome: ArcVoiceAnswerStatus.Answered,
    result,
    isBackground: true,
  })
}

export const parkedItems = (agenda: IArcVoiceAgendaItem[]) =>
  agenda.filter(({ status }) => status === ArcVoiceAgendaStatus.Parked)

/** Where an item stands, as every view of the agenda shows it; each picks its own words. */
export enum AgendaItemPhase {
  Working = 'working',
  Parked = 'parked',
  Stopped = 'stopped',
  Failed = 'failed',
  NeedsApproval = 'needsApproval',
  /** Back, and waiting for a pause to be told. */
  Ready = 'ready',
  Done = 'done',
}

export const agendaItemPhase = ({ status, outcome }: IArcVoiceAgendaItem): AgendaItemPhase => {
  if (status === ArcVoiceAgendaStatus.Working) return AgendaItemPhase.Working
  if (status === ArcVoiceAgendaStatus.Parked) return AgendaItemPhase.Parked
  if (status === ArcVoiceAgendaStatus.Stopped) return AgendaItemPhase.Stopped
  if (outcome === ArcVoiceAnswerStatus.Failed) return AgendaItemPhase.Failed
  if (outcome === ArcVoiceAnswerStatus.AwaitingApproval) return AgendaItemPhase.NeedsApproval
  if (status === ArcVoiceAgendaStatus.Ready) return AgendaItemPhase.Ready
  return AgendaItemPhase.Done
}

const CALL_ITEM_STATES: Record<AgendaItemPhase, ArcVoiceCallItemState> = {
  [AgendaItemPhase.Working]: ArcVoiceCallItemState.Running,
  [AgendaItemPhase.Parked]: ArcVoiceCallItemState.Parked,
  [AgendaItemPhase.Stopped]: ArcVoiceCallItemState.Stopped,
  [AgendaItemPhase.Failed]: ArcVoiceCallItemState.Failed,
  [AgendaItemPhase.NeedsApproval]: ArcVoiceCallItemState.NeedsApproval,
  [AgendaItemPhase.Ready]: ArcVoiceCallItemState.Done,
  [AgendaItemPhase.Done]: ArcVoiceCallItemState.Done,
}

const callItemState = (item: IArcVoiceAgendaItem): ArcVoiceCallItemState =>
  CALL_ITEM_STATES[agendaItemPhase(item)]

/** The agenda as the call's record keeps it, for the recap Arc posts when the call ends. */
export const toCallAgendaEntries = (agenda: IArcVoiceAgendaItem[]): IArcVoiceCallAgendaEntry[] =>
  agenda.slice(-ARC_VOICE_AGENDA_MAX_ITEMS).map((item) => ({
    number: item.number,
    title: truncate(item.title, ARC_VOICE_AGENDA_TEXT_MAX_CHARS),
    state: callItemState(item),
    result: item.result ? truncate(item.result, ARC_VOICE_AGENDA_TEXT_MAX_CHARS) : null,
  }))

/** Words as compared with others: the same, however they're punctuated or cased. */
export const comparable = (text: unknown): string =>
  typeof text === 'string'
    ? text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
    : ''

/**
 * An item still working on the same request, which a second hand-off would only
 * repeat. Matched on the request, not the title: the voice makes titles up, and two
 * different asks can share one.
 */
export const runningDuplicate = (
  agenda: IArcVoiceAgendaItem[],
  request: string,
): IArcVoiceAgendaItem | undefined => {
  const wanted = comparable(request)
  return workingItems(agenda).find((item) => comparable(item.request) === wanted)
}

export enum CancelTargetKind {
  /** Running, and theirs to stop. */
  Stop = 'stop',
  /** What they named isn't running: it finished, failed, or was stopped already. */
  NotRunning = 'notRunning',
  Nothing = 'nothing',
  /** They named something that matches nothing, and more than one thing is running. */
  Unclear = 'unclear',
}

export type ICancelTarget =
  | { kind: CancelTargetKind.Stop; items: IArcVoiceAgendaItem[] }
  | { kind: CancelTargetKind.NotRunning; item: IArcVoiceAgendaItem }
  | { kind: CancelTargetKind.Nothing }
  | { kind: CancelTargetKind.Unclear; running: IArcVoiceAgendaItem[] }

export interface ICancelRequest {
  number?: number
  /** The words they named it by ("the email to Acme"). */
  target?: string
  isAll?: boolean
}

/** Words that say nothing about which request is meant. */
const CANCEL_FILLER_WORDS = new Set([
  'the',
  'that',
  'this',
  'one',
  'a',
  'an',
  'to',
  'for',
  'of',
  'my',
  'it',
  'and',
  'in',
  'on',
  'with',
  'last',
  'request',
  'please',
])

/** A word as compared across a request and how they named it: "emails" finds "email". */
const stem = (word: string) => (word.length > 3 ? word.replace(/s$/, '') : word)

const contentWords = (text: string): string[] =>
  comparable(text)
    .split(' ')
    .filter((word) => word && !CANCEL_FILLER_WORDS.has(word))
    .map(stem)

/** The item whose title and request share the most words with what they called it. */
const itemNamed = (
  agenda: IArcVoiceAgendaItem[],
  target: string,
): IArcVoiceAgendaItem | undefined => {
  const wanted = contentWords(target)
  const scored = agenda
    .map((item) => {
      const words = new Set(contentWords(`${item.title} ${item.request}`))
      return { item, score: wanted.filter((word) => words.has(word)).length }
    })
    .filter(({ score }) => score > 0)
  // Ties go to what is still running, then to the latest.
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      Number(isAwaitingResult(b.item)) - Number(isAwaitingResult(a.item)) ||
      b.item.number - a.item.number,
  )
  return scored[0]?.item
}

/**
 * What "cancel that" means on this agenda: the one they named, everything when
 * they said so, or else the one started last. Something named that isn't running
 * is said to be as it is (gone through, failed, stopped already), never pretended stopped.
 */
export const cancelTarget = (
  agenda: IArcVoiceAgendaItem[],
  { number, target, isAll }: ICancelRequest,
): ICancelTarget => {
  const unresolved = agenda.filter(isAwaitingResult)
  if (isAll) {
    return unresolved.length
      ? { kind: CancelTargetKind.Stop, items: unresolved }
      : { kind: CancelTargetKind.Nothing }
  }
  const working = workingItems(agenda)
  const running = working.length ? working : unresolved
  const named =
    number !== undefined
      ? agenda.find((item) => item.number === number)
      : target && itemNamed(agenda, target)
  if (named) {
    return isAwaitingResult(named)
      ? { kind: CancelTargetKind.Stop, items: [named] }
      : { kind: CancelTargetKind.NotRunning, item: named }
  }
  if (running.length === 0) return { kind: CancelTargetKind.Nothing }
  // "That one" or "the last one" names nothing: the latest is meant.
  const isNamed = number !== undefined || (!!target && contentWords(target).length > 0)
  if (isNamed && running.length > 1) return { kind: CancelTargetKind.Unclear, running }
  return {
    kind: CancelTargetKind.Stop,
    items: [running.reduce((a, b) => (b.number > a.number ? b : a))],
  }
}

const quotedTitles = (items: IArcVoiceAgendaItem[]): string => {
  const titles = items.map(({ title }) => `"${title}"`)
  return titles.length > 1 ? `${titles.slice(0, -1).join(', ')} and ${titles.at(-1)}` : titles[0]
}

/** Every turn the item has run on: a stop tears down the plans and subagents each started. */
export const itemTurnIds = ({ id, turnId, earlierTurnIds }: IArcVoiceAgendaItem): string[] => [
  ...new Set([id, ...(earlierTurnIds ?? []), turnId ?? id]),
]

/** Which of the items a cancel was for it stopped, which had already finished, and which it couldn't reach. */
export interface ICancelOutcome {
  stopped: IArcVoiceAgendaItem[]
  /** Had already finished, so it went through. */
  finished: IArcVoiceAgendaItem[]
  /** Couldn't be stopped, and may still be running. */
  failed: IArcVoiceAgendaItem[]
  /** Had already failed on its own, so nothing went through. */
  hadFailed?: IArcVoiceAgendaItem[]
}

/** How a cancel went, for the voice to say in a sentence. */
export const cancelNote = ({ stopped, finished, failed, hadFailed = [] }: ICancelOutcome): string =>
  [
    stopped.length && `Stopped ${quotedTitles(stopped)}; nothing more will happen for it.`,
    finished.length &&
      `${quotedTitles(finished)} had already finished, so it went through; it can't be undone from here.`,
    hadFailed.length && `${quotedTitles(hadFailed)} had already failed, so it didn't go through.`,
    failed.length && `Couldn't stop ${quotedTitles(failed)}; it may still be running.`,
    'Say so in one short sentence.',
  ]
    .filter(Boolean)
    .join(' ')

/** How a Stop pressed on screen went, as a line for the screen. */
export const stopNotice = ({
  stopped,
  finished,
  failed,
  hadFailed = [],
}: ICancelOutcome): { message: string; isError: boolean } => ({
  message: [
    stopped.length && `Stopped ${quotedTitles(stopped)}.`,
    finished.length && `${quotedTitles(finished)} had already finished.`,
    hadFailed.length && `${quotedTitles(hadFailed)} had already failed.`,
    failed.length && `Couldn’t stop ${quotedTitles(failed)}; it may still be running.`,
  ]
    .filter(Boolean)
    .join(' '),
  isError: failed.length > 0,
})

const notRunningNote = (item: IArcVoiceAgendaItem): string => {
  const title = `"${item.title}"`
  if (item.status === ArcVoiceAgendaStatus.Stopped) {
    return `${title} was already stopped; nothing more will happen for it. Say so in one short sentence.`
  }
  if (item.outcome === ArcVoiceAnswerStatus.Failed) {
    return `${title} didn't go through, so there was nothing to stop. Say so in one short sentence.`
  }
  if (item.outcome === ArcVoiceAnswerStatus.AwaitingApproval) {
    return `${title} is waiting on their approval on screen and won't run unless they approve it; they can turn it down there. Say so in one short sentence.`
  }
  return cancelNote({ stopped: [], finished: [item], failed: [] })
}

/** Why nothing was stopped, for the voice to say. */
export const nothingToCancelNote = (target: ICancelTarget): string => {
  if (target.kind === CancelTargetKind.Unclear) {
    return `More than one thing is running (${quotedTitles(target.running)}) and it isn't clear which they mean. Ask which, in a few words.`
  }
  if (target.kind === CancelTargetKind.NotRunning) return notRunningNote(target.item)
  return 'Nothing from this call is running, so nothing was stopped. Say so in a few words.'
}
